import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import styles from '../styles.module.css';
import { Company, PaginationParams } from './types';
import CompanyIcon from '@/assets/icons/union.svg?react';
import {
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import Checkbox from '@/components/Form/Checkbox';
import PlusButton from '@/components/Form/PlusButton';
import SearchInput from '@/components/Form/searchInput';
import Pagination from '@/components/Pagination';
import { PaginationRequestParams } from '@/components/Pagination/usePaginationTokens';
import { adminToolPaths } from '@/constants/adminTool';
import { PermissionsList } from '@/constants/permissionsList';
import { useToast } from '@/context/ToastContext';
import { useGetCompaniesInitial } from '@/hooks/services/Companies';
import { RootState } from '@/store';
import { getUsersPermissions } from '@/utils/adminToolPermissions';

const API_PAGE_SIZE = 40;
const CLIENT_PAGE_SIZE = 10;

function CompaniesContainer() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const { canCreate: canCreateCompany } = getUsersPermissions(
    auditableElements,
    userGroup
  );

  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    pageSize: CLIENT_PAGE_SIZE,
    paginationToken: undefined,
    method: 'firstPage'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showActive, setShowActive] = useState(true);
  const [showInactive, setShowInactive] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [apiStart, setApiStart] = useState(0);
  const [accumulatedCompanies, setAccumulatedCompanies] = useState<Company[]>(
    []
  );
  const [hasMoreFromApi, setHasMoreFromApi] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
    setApiStart(0);
    setAccumulatedCompanies([]);
    setHasMoreFromApi(true);
    setPaginationParams(prev => ({
      ...prev,
      paginationToken: undefined,
      pageNumber: undefined,
      method: 'firstPage'
    }));
  }, [debouncedSearchTerm]);

  const {
    data: rawResponse,
    isLoading: loading,
    error: queryError,
    refetch
  } = useGetCompaniesInitial({
    searchParam: debouncedSearchTerm,
    useQueryOptions: { enabled: true },
    start: apiStart
  });

  useEffect(() => {
    if (loading || !rawResponse?.data) return;
    const data = (rawResponse.data ?? []) as unknown as Company[];
    const count = data.length;
    setAccumulatedCompanies(prev =>
      apiStart === 0 ? data : [...prev, ...data]
    );
    if (apiStart === 0) {
      setIsInitialLoad(false);
    }
    if (count < API_PAGE_SIZE) {
      setHasMoreFromApi(false);
    }
  }, [loading, rawResponse, apiStart]);

  const companies = useMemo(() => accumulatedCompanies, [accumulatedCompanies]);
  const error = queryError ? (queryError as Error).message : null;

  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const isActive = company.isActive === '1';
      const matchesActive = showActive && isActive;
      const matchesInactive = showInactive && !isActive;
      return matchesActive || matchesInactive;
    });
  }, [companies, showActive, showInactive]);

  const paginatedCompanies = useMemo(() => {
    const startIndex = (currentPage - 1) * paginationParams.pageSize;
    return filteredCompanies.slice(
      startIndex,
      startIndex + paginationParams.pageSize
    );
  }, [filteredCompanies, currentPage, paginationParams.pageSize]);

  useEffect(() => {
    const clientPages = Math.max(
      1,
      Math.ceil(filteredCompanies.length / paginationParams.pageSize)
    );
    setTotalPages(
      hasMoreFromApi ? Math.max(clientPages, currentPage + 1) : clientPages
    );
    setNextPageToken(null);
  }, [
    filteredCompanies.length,
    paginationParams.pageSize,
    currentPage,
    hasMoreFromApi
  ]);

  const handlePageChange = (params: PaginationRequestParams) => {
    let newPage = currentPage;
    if (params.pageNumber != null) {
      newPage = params.pageNumber;
      setCurrentPage(params.pageNumber);
    } else if (params.direction === 'next') {
      newPage = Math.min(currentPage + 1, totalPages);
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    } else if (params.direction === 'previous') {
      newPage = Math.max(currentPage - 1, 1);
      setCurrentPage(prev => Math.max(prev - 1, 1));
    }
    if (newPage === totalPages && hasMoreFromApi && !loading) {
      setApiStart(prev => prev + API_PAGE_SIZE);
    }
  };

  const handleCompanyClick = (id: string) => {
    navigate(adminToolPaths.companies.detail.replace(':companyId', id));
  };

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  useEffect(() => {
    if (error) {
      showToast(error || 'An error occurred while loading companies', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => refetch()
      });
    }
  }, [error]);

  if (loading && isInitialLoad) {
    return <ContainerLoader />;
  }

  const columns: DataTableColumn[] = [
    {
      field: 'name',
      headerName: 'COMPANY NAME',
      width: '50%',
      sortable: true,
      onClick: (row: Company) => {
        handleCompanyClick(row?.id || '');
      }
    },
    {
      field: 'countLocations',
      headerName: '# OF LOCATIONS',
      width: 'auto',
      sortable: true,
      valueGetter: (_, row: Company) => row?.countLocations || 0
    },
    {
      field: 'countTurbines',
      headerName: '# OF TURBINES',
      valueGetter: (_, row: Company) => row?.countTurbines || 0,
      width: 'auto',
      sortable: true
    },
    {
      field: 'city',
      headerName: 'CITY',
      width: 'auto',
      sortable: true,
      valueGetter: (_, row: Company) => row?.city || ''
    },
    {
      field: 'stateProv',
      headerName: 'STATE',
      width: 'auto',
      sortable: true,
      valueGetter: (_, row: Company) => row?.stateProv || ''
    }
  ];

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <CompanyIcon className={styles.adminIconHeader} />
              <h2 className={styles.adminTitle}>Companies</h2>
            </>
          }
          center={
            <div className={styles.adminContainerHeaderCenter}>
              <SearchInput
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search"
              />
              <Checkbox
                checked={showActive}
                onChange={setShowActive}
                label="Active"
              />
              <Checkbox
                checked={showInactive}
                onChange={setShowInactive}
                label="Inactive"
              />
            </div>
          }
          right={
            canCreateCompany ? (
              <PlusButton
                onClick={() => navigate(adminToolPaths.companies.newCompany)}
                label="Add a new company"
              />
            ) : null
          }
        />
        <AdminTableBody>
          {loading ? (
            <div className={styles.adminLoadingTableWrapper}>
              <ContainerLoader />
            </div>
          ) : (
            <>
              <DataTable
                rows={paginatedCompanies}
                columns={columns}
                initialState={{ sorting: { field: 'name', sort: 'asc' } }}
              />
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                nextPageToken={nextPageToken}
                pageSize={paginationParams.pageSize}
                onPageChange={handlePageChange}
                useTokens={false}
              />
            </>
          )}
        </AdminTableBody>
      </div>
    </div>
  );
}

export default CompaniesContainer;
